import { createHeadersFormOptions } from "./fetch";
import { checkAppVersion } from "./utils";
import { API_URL } from "./API";
import { handleAuthStatus } from "../utils/authSession";

/**
 * Shared replacement for the legacy `fetchJson` transport.
 *
 * `fetchJson` resolves for every response and calls JSON.parse unconditionally, so a
 * non-2xx with a plain-text body surfaces as a SyntaxError and the backend's actual
 * reason is lost. Several endpoints now return plain-text 400s (e.g.
 * "Invalid IANA timezone: +05:00") that the user is expected to act on, so callers need
 * the real message.
 *
 * Unlike the ad-hoc copies of this helper, this one still calls checkAppVersion, which is
 * what triggers the reload prompt after a deploy.
 */

export const buildApiUrl = (path) => `${API_URL}/${path}`;

/**
 * `statusMessages` lets a caller override the message for a given status, e.g.
 * `{ 403: "You need admin access" }`, since the backend's own wording is often generic.
 */
export const extractErrorMessage = (text, status, statusMessages = {}) => {
  if (statusMessages[status]) return statusMessages[status];
  if (!text) return `Request failed (${status})`;

  try {
    const parsed = JSON.parse(text);
    return (
      parsed?.message ||
      parsed?.error ||
      parsed?.detail ||
      (typeof parsed === "string" ? parsed : `Request failed (${status})`)
    );
  } catch (_) {
    // Plain-text body — the common case for this app's error handler.
    return text || `Request failed (${status})`;
  }
};

/**
 * Full response: `{ json, headers, status }`. Use when a response header matters
 * (e.g. X-Timezones-Narrowed). Throws on non-2xx; tolerates an empty body.
 */
export const requestRaw = async (url, options = {}, statusMessages) => {
  const headers = createHeadersFormOptions(options);
  const response = await fetch(url, { ...options, headers });
  const text = await response.text();

  // Run before the status check so a deploy is still detected on an error response.
  checkAppVersion(response.headers.get("x-version"));
  handleAuthStatus(response.status);

  if (!response.ok) {
    throw new Error(extractErrorMessage(text, response.status, statusMessages));
  }

  let json = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch (_) {
      json = text;
    }
  }

  return { json, headers: response.headers, status: response.status };
};

export const requestJson = async (url, options, statusMessages) =>
  (await requestRaw(url, options, statusMessages)).json;
